import { useEffect } from 'react';
import { Message } from '@oneflowab/pomes';
import type { MessageTranslator } from '@oneflowab/pomes';
import { useDispatch, useSelector } from 'react-redux';
import { getMoment, addDaysToDate } from 'date';

import adminPage from 'hocs/admin-page';
import apiTokensReducer from 'reducers/entities/api-tokens';
import { getExtensionsById } from 'reducers/entities/extensions';

import AddApiToken from 'components/modals/add-api-token';
import { Table } from 'components/table';
import Tooltip from 'components/tooltip';
import { GeneratedApiToken } from 'components/extension/generated-token';
import { LocalizedDateTime } from 'components/localized-date-time';
import Button from 'components/button';
import AddIcon from 'components/icons/add';
import WarningIcon from 'components/icons/warning';
import ActionsMenu from 'components/actions-menu';
import { DeleteMenuItem } from 'components/menu-items/delete';
import { RevokeApiTokenConfirmation } from 'components/modals/revoke-api-token';
import ActionBar from 'components/action-bar';
import { ExtensionName } from 'components/extension/extension-name';

import style from './api-tokens.module.scss';

export const QUERY_NAME = '/admin/account/extensions/api-tokens';

export const ApiTokensComponent = ({ message }: { message: MessageTranslator }) => {
  const dispatch = useDispatch();

  const query = useSelector((state) => apiTokensReducer.getQuerySelector(state, {
    name: QUERY_NAME,
  }));

  const apiTokens = useSelector((state) => apiTokensReducer.getApiTokensSelector(state, {
    ids: query.result,
  }));

  const extensionsById = useSelector(getExtensionsById);

  useEffect(() => {
    dispatch(apiTokensReducer.queryApiTokens({
      name: QUERY_NAME,
    }));
  }, [dispatch]);

  const createState = useSelector((state) => apiTokensReducer.getCreateSelector(state));
  const createdApiToken = useSelector((state) => apiTokensReducer.getApiTokenSelector(state, {
    id: createState.result ?? 0,
  }));

  const getName = (apiToken: Oneflow.ApiToken) => {
    const isTokenInactive = (
      (!apiToken.lastUsedTime && getMoment() > addDaysToDate(apiToken.createdTime, 365))
      || (apiToken.lastUsedTime && getMoment() > addDaysToDate(apiToken.lastUsedTime, 365))
    );

    return (
      <span>
        {apiToken.description}
        {isTokenInactive ? (
          <Tooltip
            message={message({
              id: 'API token has not been used in the past 365 days',
              comment: 'Tooltip warning on inactive tokens.',
            })}
            side="right"
          >
            <span>
              <WarningIcon className={style.InactiveTokenWarning} />
            </span>
          </Tooltip>
        ) : null}
      </span>
    );
  };

  const getActiveState = ({ active }: Oneflow.ApiToken) => {
    if (active) {
      return (
        <Message
          id="Active"
          comment="Text explaining that the api token is active."
        />
      );
    }

    return (
      <Message
        id="Inactive"
        comment="Text explaining that the api token is inactive."
      />
    );
  };
  const getUsedByLink = (apiToken: Oneflow.ApiToken) => {
    if (!apiToken.usedBy) {
      return '-';
    }

    const extensionUsingApiToken = extensionsById[apiToken.usedBy as keyof typeof extensionsById];

    if (!extensionUsingApiToken) {
      return '-';
    }

    return (
      <ExtensionName extension={extensionUsingApiToken} isLinked />
    );
  };

  const getLastUsedDate = (apiToken: Oneflow.ApiToken) => {
    if (!apiToken.lastUsedTime) {
      return '-';
    }

    return (
      <LocalizedDateTime datetime={apiToken.lastUsedTime} />
    );
  };

  const getCreatedDate = (apiToken: Oneflow.ApiToken) => (
    <LocalizedDateTime datetime={apiToken.createdTime} />
  );

  const getDeleteMenuItem = (onClick: () => void) => (
    <DeleteMenuItem
      onClick={onClick}
    />
  );

  const getActions = (apiToken: Oneflow.ApiToken) => {
    if (!apiToken.usedBy) {
      return (
        <ActionsMenu
          actions={[
            <RevokeApiTokenConfirmation key="revoke" apiToken={apiToken}>
              {getDeleteMenuItem}
            </RevokeApiTokenConfirmation>,
          ]}
          focusOnCloseDisabled
        />
      );
    }

    return (
      <Tooltip
        message={message({
          id: 'Not allowed to revoke API tokens created from other extensions',
          comment: 'Tooltip showing reason for not being allowed to remove API token.',
        })}
        side="top"
      >
        <ActionsMenu
          actions={[]}
          disabled
          focusOnCloseDisabled
        />
      </Tooltip>
    );
  };

  const getTableConfig = () => {
    const nameColumn = {
      name: 'name',
      label: message({
        id: 'Name',
        comment: 'Column label in api token list',
      }),
      type: 'cell',
      value: getName,
    };

    const activeColumn = {
      name: 'active',
      label: message({
        id: 'Active',
        comment: 'Column label in api token list',
      }),
      type: 'cell',
      value: getActiveState,
    };
    const usedByColumn = {
      name: 'usedBy',
      label: message({
        id: 'Used by',
        comment: 'Column label in api token list',
      }),
      type: 'cell',
      value: getUsedByLink,
    };
    const lastUsedColumn = {
      name: 'lastUsedTime',
      label: message({
        id: 'Last used',
        comment: 'Column label in api token list',
      }),
      type: 'cell',
      value: getLastUsedDate,
    };
    const createdColumn = {
      name: 'createdTime',
      label: message({
        id: 'Created',
        comment: 'Column label in api token list',
      }),
      type: 'cell',
      value: getCreatedDate,
    };
    const actionsColumn = {
      name: 'actions',
      label: message({
        id: 'Actions',
        comment: 'Column label in api token list',
      }),
      type: 'actions',
      value: getActions,
    };

    return {
      items: apiTokens,
      itemKey: 'id',
      actions: [],
      columns: [
        nameColumn,
        activeColumn,
        usedByColumn,
        lastUsedColumn,
        createdColumn,
        actionsColumn,
      ],
    };
  }

  const getGenerateNewTokenButton = (onClick?: React.MouseEventHandler<HTMLButtonElement>) => (
    <Button
      data-testid="add-api-token"
      icon={AddIcon}
      kind="primary"
      onClick={onClick}
    >
      <Message
        id="Generate a new token"
        comment="Button text for adding API token on API token list page."
      />
    </Button>
  );

  return (
    <div>
      <ActionBar>
        <ActionBar.Group>
          <AddApiToken>
            {getGenerateNewTokenButton}
          </AddApiToken>
        </ActionBar.Group>
      </ActionBar>
      <div>
        <Table
          config={getTableConfig()}
          query={query}
        />
      </div>
      <GeneratedApiToken tokenEntity={createdApiToken} canRegenerateToken />
    </div>
  );
};

type MapperProps = {
  message: MessageTranslator,
};

const propsMapper = ({ props: { message } }: { props: MapperProps }) => ({
  modules: [[]],
  section: message({ id: 'Workflows', comment: 'Used as the title for the section.' }),
  showWorkspace: true,
  title: message({ id: 'Api tokens', comment: 'Page title for the api tokens page.' }),
});

export default adminPage(propsMapper)(ApiTokensComponent);